import type { FormFieldConfig, FormFieldType } from './types'

// ─── Server-driven form config ────────────────────────────────────────────────
// The FeelGreat settings response ships per-country form definitions
// (`createYourAccount.inputs`, `shippingAddress.inputs`). These map to the core
// FormFieldConfig so plugins can render country-specific checkout forms without
// hardcoding the field list.

export interface SettingsFormInput {
    key: string
    label?: string
    /** Backend field type: text | password | CountryBox | AutoAddress | select | … */
    type?: string
    value?: string
    args?: {
        disabled?: boolean
        onEnroll?: boolean
        regx?: string
    } | null
}

export interface SettingsFormConfig {
    inputs?: SettingsFormInput[]
}

// Backend field type → core FormFieldType. Unknown types fall back to 'text'.
const DEFAULT_TYPE_MAP: Record<string, FormFieldType> = {
    text: 'text',
    email: 'email',
    password: 'password',
    phone: 'phone',
    select: 'select',
    CountryBox: 'country_readonly',
    AutoAddress: 'address'
}

export interface BuildFieldsFromSettingsOptions {
    /** Force a core type per key (settings key or mapped key), e.g. `{ city: 'select' }`. */
    typeByKey?: Record<string, FormFieldType>
    /** Override the label (dict key) per key, e.g. `{ phone: 'phone' }`. */
    labelByKey?: Record<string, string>
    /** Remap a settings key to an internal form key, e.g. `{ street_address: 'address1' }`. */
    keyMap?: Record<string, string>
    /** Assign a row per key for multi-column layout; unmapped keys get their own row. */
    rowByKey?: Record<string, number>
    /** Drop these settings keys entirely. */
    exclude?: string[]
    /** Mark these (post-map) keys optional. */
    optional?: string[]
    /** Merge extra props per (post-map) key (defaultValue, linkText, helperText, …). */
    extraByKey?: Record<string, Partial<FormFieldConfig>>
}

/**
 * Map a settings form-input list to core FormFieldConfig[]. Output is sorted by
 * the resolved row so `rowByKey` fully controls layout regardless of the input
 * order. `keyByKey`/`labelByKey`/`typeByKey` are looked up by both the mapped
 * key and the original settings key.
 */
export const buildFieldsFromSettings = (inputs: SettingsFormInput[] | undefined, opts: BuildFieldsFromSettingsOptions = {}): FormFieldConfig[] => {
    const { typeByKey = {}, labelByKey = {}, keyMap = {}, rowByKey = {}, exclude = [], optional = [], extraByKey = {} } = opts

    const list = Array.isArray(inputs) ? inputs : []
    const fields: FormFieldConfig[] = []
    let autoRow = 1

    for (const input of list) {
        const settingsKey = input?.key
        if (!settingsKey || exclude.includes(settingsKey)) continue

        const key = keyMap[settingsKey] ?? settingsKey
        const type = typeByKey[key] ?? typeByKey[settingsKey] ?? DEFAULT_TYPE_MAP[input.type ?? 'text'] ?? 'text'
        const label = labelByKey[key] ?? labelByKey[settingsKey] ?? input.label ?? key
        const row = rowByKey[key] ?? rowByKey[settingsKey] ?? autoRow++

        const field: FormFieldConfig = { key, label, type, row }
        if (optional.includes(key)) field.optional = true

        fields.push({ ...field, ...extraByKey[key] })
    }

    // Stable sort by row so grouping (groupFieldsByRow) emits rows in order even
    // when the settings input order doesn't match the desired layout.
    return fields
        .map((field, index) => ({ field, index }))
        .sort((a, b) => a.field.row - b.field.row || a.index - b.index)
        .map(({ field }) => field)
}

/**
 * Union of several settings input groups, deduped by key (first occurrence
 * wins). Mirrors the legacy guest form = createYourAccount ∪ shippingAddress,
 * where createYourAccount defines the account fields and shippingAddress adds
 * the address fields on top.
 */
export const mergeSettingsInputs = (...groups: Array<SettingsFormInput[] | undefined>): SettingsFormInput[] => {
    const byKey = new Map<string, SettingsFormInput>()
    for (const group of groups) {
        for (const input of group ?? []) {
            if (input?.key && !byKey.has(input.key)) byKey.set(input.key, input)
        }
    }
    return [...byKey.values()]
}
